// Public-facing match codes.
//
// Internal match ids stay exactly as generated — quals `G15` (3v3) / `E15` (1v1),
// elims `U_UB1` / `E_LB5` (the slot name IS the id; the bracket progression RPC
// parses it to route winners/losers). We never rename them. For DISPLAY only we
// map each id to a friendly, per-event, incrementing code:
//
//   3v3 / 1v1 quals   G15  / E15    → 3v3_15  / 1v1_15   (the qual sequence number)
//   3v3 / 1v1 elims   U_UB1 / E_LB5 → 3v3_101+ / 1v1_101+ (101-based, in PLAY ORDER)
//   EC / ARCTIC / practice / anything unrecognized → passed through unchanged.
//
// Elims start at 101 (a gap after quals) and increment in the order the matches
// are actually played. Applied centrally in <ProtoMatchCode>, so every code the
// app renders is friendly without touching any storage or bracket logic.

import { PLAYOFF_SCHEDULE } from '../state/schedule';

// Elim slot → 101-based number, ordered by the slot's scheduled time (play
// order). Derived from PLAYOFF_SCHEDULE so it can't drift from the actual elim
// timeline — e.g. LB5 scheduled before UB7 ⇒ LB5 gets the lower number.
function elimNumbers(event: '3v3' | '1v1'): Record<string, number> {
  const out: Record<string, number> = {};
  Object.entries(PLAYOFF_SCHEDULE[event])
    .sort((a, b) => a[1].localeCompare(b[1])) // ISO `…Z` strings sort chronologically
    .forEach(([slot], i) => { out[slot] = 101 + i; });
  return out;
}
const ELIM_NUM: Record<'3v3' | '1v1', Record<string, number>> = {
  '3v3': elimNumbers('3v3'),
  '1v1': elimNumbers('1v1'),
};

/**
 * Friendly, public-facing code for a match id. Pure function of the id (the id
 * pattern alone encodes event + qual/playoff). Unknown shapes pass through, so
 * it's safe to apply to any code (EC, ARCTIC, practice, etc.).
 */
export function publicMatchCode(id: string): string {
  // Playoffs: prefix `U_` (3v3) / `E_` (1v1) + slot (UB1, LB5, GF, …).
  if (id.startsWith('U_')) { const s = id.slice(2); return `3v3_${ELIM_NUM['3v3'][s] ?? s}`; }
  if (id.startsWith('E_')) { const s = id.slice(2); return `1v1_${ELIM_NUM['1v1'][s] ?? s}`; }
  // Quals: `G<n>` (3v3) / `E<n>` (1v1).
  const g = /^G(\d+)$/.exec(id); if (g) return `3v3_${g[1]}`;
  const e = /^E(\d+)$/.exec(id); if (e) return `1v1_${e[1]}`;
  // EC / ARCTIC / practice / unknown → unchanged.
  return id;
}

/**
 * Numeric sort key behind a match's public code: quals by sequence number (1+),
 * elims 101+ in PLAY order, EC / ARCTIC / practice by their trailing number.
 * Higher = played later / more recent — so sorting descending lists results
 * most-recent-first with the elim bracket in correct order (a plain id sort put
 * E_GF / E_LB1 / E_UB1 in alphabetical, not play, order). Returns 0 for ids with
 * no number.
 */
export function publicMatchNumber(id: string): number {
  if (id.startsWith('U_')) return ELIM_NUM['3v3'][id.slice(2)] ?? 0;
  if (id.startsWith('E_')) return ELIM_NUM['1v1'][id.slice(2)] ?? 0;
  const m = /(\d+)$/.exec(id);
  return m ? parseInt(m[1], 10) : 0;
}
